/**
 * Department Reconciliation — one-time governance screen.
 *
 * Lists every department held by active users that resolves to NO department
 * permission profile. Those users get a blank sidebar AND 403s on every API in
 * enforce mode. Reassignment goes through the Master-Lists bulk-reassign path so
 * users + employees + access_department propagate together (one audit record per
 * user). Leadership users can be given a senior profile instead of a department
 * profile.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Loader2, AlertTriangle, ShieldCheck, Users, ChevronDown, ChevronRight, ArrowRight, RefreshCw, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPut, apiPost } from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';

export const DepartmentReconciliationPanel = ({ canEdit }) => {
  const { checkAuth } = useAuth() || {};
  const [data, setData] = useState(null);
  const [diag, setDiag] = useState(null);
  const [seniors, setSeniors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [target, setTarget] = useState({});        // dept -> canonical target
  const [seniorFor, setSeniorFor] = useState({});  // dept -> senior profile
  const [picked, setPicked] = useState({});        // user_id -> bool
  const [busy, setBusy] = useState('');
  const [restorePreview, setRestorePreview] = useState(null);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, d, s] = await Promise.all([
        apiGet('/rbac/v2/diagnostics/orphan-departments'),
        apiGet('/rbac/v2/diagnostics/visibility?include_rows=false'),
        apiGet('/rbac/v2/senior-profiles').catch(() => ({ profiles: [] })),
      ]);
      setData(o);
      setDiag(d);
      setSeniors((s.profiles || []).map(p => p.name));
      const pre = {};
      (o.orphans || []).forEach(x => { if (x.suggested_target) pre[x.department] = x.suggested_target; });
      setTarget(t => ({ ...pre, ...t }));
    } catch (e) {
      toast.error(e?.message || 'Could not load reconciliation data');
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const reassign = async (dept, userIds) => {    const newDept = target[dept];
    if (!newDept) { toast.error('Pick a target department first'); return; }
    if (!userIds.length) { toast.error('No users selected'); return; }
    setBusy(dept);
    try {
      const res = await apiPut('/masters/departments/bulk-reassign', {
        user_ids: userIds,
        new_department: newDept,
        senior_profile: seniorFor[dept] || null,
      });
      toast.success(`${res.users_updated} user(s) moved to ${newDept}`);
      setPicked({});
      await load();
      if (checkAuth) checkAuth();
    } catch (e) {
      toast.error(e?.message || 'Reassignment failed');
    }
    setBusy('');
  };

  const restore = async (dept, source) => {
    setBusy(dept);
    try {
      const dry = await apiPost(`/rbac/v2/department-profiles/${encodeURIComponent(dept)}/restore?source=${source}&dry_run=true`, {});
      if (!dry.granted_nodes_proposed) { toast.error('Nothing to restore from that source'); setBusy(''); return; }
      setRestorePreview({ dept, source, ...dry });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Restore preview failed');
    }
    setBusy('');
  };

  const commitRestore = async () => {
    const { dept, source } = restorePreview;
    setBusy(dept);
    try {
      const res = await apiPost(`/rbac/v2/department-profiles/${encodeURIComponent(dept)}/restore?source=${source}&dry_run=false`, {});
      toast.success(`${dept}: ${res.granted_nodes_proposed} permission(s) restored`);
      setRestorePreview(null);
      await load();
      if (checkAuth) checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Restore failed');
    }
    setBusy('');
  };

  // Department_Single_Source_Of_Truth_Fix Item 4 — one-time cleanup: resolve
  // any still-drifted employee department at high confidence, drop leftover
  // access_department values from past runs, and flag (never auto-change) the
  // one PM edge case that needs a human look.
  const runMigration = async () => {
    setMigrating(true);
    try {
      const res = await apiPost('/rbac/v2/diagnostics/collapse-access-department', {});
      setMigrationResult(res);
      toast.success(`Migration done · ${res.departments_auto_fixed?.length || 0} department(s) auto-fixed, ${res.employees_access_department_dropped} employee + ${res.users_access_department_dropped} user field(s) cleaned up`);
      await load();
      if (checkAuth) checkAuth();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Migration failed');
    }
    setMigrating(false);
  };

  if (loading) {
    return <div className="p-10 text-center text-sm text-slate-500" data-testid="dept-recon-loading">
      <Loader2 size={18} className="animate-spin inline mr-2" />Auditing department resolution…
    </div>;
  }

  const orphans = data?.orphans || [];
  const consultantsBroken = data?.consultants_without_profile || [];
  const profiles = data?.canonical_profiles || [];
  const emptyProfiles = data?.empty_profiles || [];

  return (
    <div className="space-y-5" data-testid="dept-recon-panel">
      {/* Summary strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label="RBAC mode" value={diag?.rbac_mode || '—'} testid="recon-stat-mode" />
        <Stat label="Users audited" value={diag?.users_checked ?? '—'} testid="recon-stat-users" />
        <Stat label="Engine ↔ sidebar divergences" value={diag?.divergent_users ?? '—'}
              tone={diag?.divergent_users ? 'rose' : 'emerald'} testid="recon-stat-divergent" />
        <Stat label="Users with no profile" value={data?.orphan_user_count ?? '—'}
              tone={data?.orphan_user_count ? 'rose' : 'emerald'} testid="recon-stat-orphans" />
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-500 max-w-3xl">
          Sidebar visibility and API enforcement now share one resolver. Anything listed below is a
          <strong> data</strong> problem, not a code one: the department has no permission profile, so the
          user sees no modules and every API returns 403.
        </p>
        <button onClick={load} data-testid="recon-refresh-btn"
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-slate-600 border border-slate-300 rounded-sm hover:bg-slate-50">
          <RefreshCw size={12} /> Re-run audit
        </button>
      </div>

      {/* Item 4 — one-time department-field cleanup migration */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-4 flex items-center justify-between gap-4" data-testid="recon-migration-panel">
        <div className="text-xs text-indigo-900">
          <div className="font-display font-bold uppercase tracking-wider text-[11px] mb-0.5">One-time cleanup migration</div>
          <p className="text-indigo-800/80 max-w-2xl">
            <code>department</code> is now the single source of truth — the old, independently-editable
            <code> access_department</code> field is retired. Run this once to auto-fix high-confidence
            drifted employee departments, drop any leftover <code>access_department</code> values, and flag
            (never auto-change) any Project Manager edge case that needs a human look.
          </p>
        </div>
        <button disabled={!canEdit || migrating} onClick={runMigration} data-testid="recon-run-migration-btn"
          className="shrink-0 flex items-center gap-1.5 px-3 py-2 text-xs font-display font-bold uppercase tracking-wider text-white bg-[#3b4872] hover:bg-[#2d3856] rounded-sm disabled:opacity-40">
          {migrating ? <Loader2 size={12} className="animate-spin" /> : <Wrench size={12} />}
          Run Migration
        </button>
      </div>

      {migrationResult && (
        <div className="border border-slate-200 rounded-sm bg-white p-4 space-y-3" data-testid="recon-migration-result">
          <div className="text-xs font-display font-bold uppercase tracking-wider text-slate-600">Migration result</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Departments auto-fixed" value={migrationResult.departments_auto_fixed?.length || 0} testid="mig-stat-fixed" />
            <Stat label="Employee fields dropped" value={migrationResult.employees_access_department_dropped ?? 0} testid="mig-stat-emp-dropped" />
            <Stat label="User fields dropped" value={migrationResult.users_access_department_dropped ?? 0} testid="mig-stat-usr-dropped" />
            <Stat label="PM edge cases flagged" value={migrationResult.pm_edge_cases_flagged?.length || 0}
                  tone={migrationResult.pm_edge_cases_flagged?.length ? 'rose' : 'emerald'} testid="mig-stat-pm" />
          </div>
          {migrationResult.pm_edge_cases_flagged?.length > 0 && (
            <div className="bg-rose-50 border border-rose-200 rounded-sm p-3 text-xs text-rose-900" data-testid="mig-pm-flagged">
              <div className="font-bold mb-1">Needs a human look — department is "Senior Leadership" with no senior_profile and no executive_role:</div>
              <ul className="space-y-0.5">
                {migrationResult.pm_edge_cases_flagged.map(p => (
                  <li key={p.id}>{p.name} · {p.email} {p.designation ? `· ${p.designation}` : ''}</li>
                ))}
              </ul>
            </div>
          )}
          {migrationResult.departments_auto_fixed?.length > 0 && (
            <div className="text-xs text-slate-600" data-testid="mig-fixed-list">
              {migrationResult.departments_auto_fixed.map((f, i) => (
                <div key={i}>{f.name}: "{f.from}" → "{f.to}"</div>
              ))}
            </div>
          )}
        </div>
      )}

      {consultantsBroken.length > 0 && (
        <div className="bg-rose-50 border border-rose-200 rounded-sm p-4" data-testid="recon-consultants-broken">
          <div className="flex items-center gap-2 text-xs font-display font-bold uppercase tracking-wider text-rose-800">
            <AlertTriangle size={13} /> {consultantsBroken.length} consultant-department user(s) with NO consultant profile — hard locked out
          </div>
          <ul className="mt-2 space-y-1 text-xs text-rose-900">
            {consultantsBroken.map(u => (
              <li key={u.user_id}>{u.name} · {u.email} {u.designation ? `· ${u.designation}` : ''}</li>
            ))}
          </ul>
        </div>
      )}

      {emptyProfiles.length > 0 && (
        <div className="border border-amber-300 rounded-sm bg-amber-50" data-testid="recon-empty-profiles">
          <div className="px-4 py-3 border-b border-amber-200 text-xs font-display font-bold uppercase tracking-wider text-amber-900 flex items-center gap-2">
            <AlertTriangle size={13} /> Department profiles that grant (almost) nothing
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-amber-800 border-b border-amber-200">
                <th className="px-4 py-2">Department</th>
                <th className="py-2">Granted nodes</th>
                <th className="py-2">Users affected</th>
                <th className="py-2 text-right pr-4">Restore</th>
              </tr>
            </thead>
            <tbody>
              {emptyProfiles.map(p => (
                <tr key={p.department} className="border-b border-amber-100" data-testid={`recon-empty-${p.department}`}>
                  <td className="px-4 py-2 font-medium text-slate-800">
                    {p.department}
                    {p.severity === 'critical' && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-rose-200 text-rose-800 font-bold">GRANTS NOTHING</span>
                    )}
                  </td>
                  <td className="py-2">{p.granted_nodes}</td>
                  <td className="py-2">{p.user_count}</td>
                  <td className="py-2 pr-4 text-right space-x-2">
                    <button disabled={!canEdit || busy === p.department} onClick={() => restore(p.department, 'history')}
                      data-testid={`recon-restore-history-${p.department}`}
                      className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-[#3b4872] border border-[#3b4872] rounded-sm hover:bg-white disabled:opacity-40">
                      From history
                    </button>
                    <button disabled={!canEdit || busy === p.department} onClick={() => restore(p.department, 'derived')}
                      data-testid={`recon-restore-derived-${p.department}`}
                      className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-white bg-[#3b4872] rounded-sm hover:bg-[#2d3856] disabled:opacity-40">
                      Rebuild baseline
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {restorePreview && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6" data-testid="recon-restore-modal">
          <div className="bg-white rounded-sm max-w-lg w-full">
            <div className="px-5 py-3 border-b border-slate-200 font-display font-bold text-sm">
              Restore {restorePreview.dept} — {restorePreview.source}
            </div>
            <div className="px-5 py-4 text-xs text-slate-600 space-y-2">
              <p>
                Current: <strong>{restorePreview.granted_nodes_now}</strong> granted node(s) →
                after restore: <strong>{restorePreview.granted_nodes_proposed}</strong>.
              </p>
              <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-sm p-2 font-mono text-[11px] text-slate-500">
                {Object.entries(restorePreview.proposed_permissions || {}).map(([k, v]) => (
                  <div key={k}>{k} → {v.level}</div>
                ))}
              </div>
            </div>
            <div className="px-5 py-3 border-t border-slate-200 flex justify-end gap-2">
              <button onClick={() => setRestorePreview(null)} data-testid="recon-restore-cancel"
                className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider border border-slate-300 rounded-sm">Cancel</button>
              <button onClick={commitRestore} disabled={!!busy} data-testid="recon-restore-confirm"
                className="px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-white bg-[#3b4872] rounded-sm disabled:opacity-50">
                Apply restore
              </button>
            </div>
          </div>
        </div>
      )}

      {orphans.length === 0 && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-5 flex items-center gap-2 text-sm text-emerald-900"
             data-testid="recon-clean">
          <ShieldCheck size={16} /> Every active user's department resolves to a permission profile. Nothing to reconcile.
        </div>
      )}

      {orphans.map(o => {
        const open = !!expanded[o.department];
        const movable = o.users.filter(u => u.user_id);
        const selectedIds = movable.filter(u => picked[u.user_id]).map(u => u.user_id);
        return (
          <div key={o.department} className="border border-slate-200 rounded-sm bg-white"
               data-testid={`recon-dept-${o.department}`}>
            <div className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-slate-100">
              <button onClick={() => setExpanded(e => ({ ...e, [o.department]: !open }))}
                className="flex items-center gap-1.5 text-sm font-display font-bold text-slate-800"
                data-testid={`recon-toggle-${o.department}`}>
                {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                {o.department}
                <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 text-[11px] font-bold">
                  <Users size={10} /> {o.user_count}
                </span>
              </button>
              <div className="flex-1" />
              <select value={target[o.department] || ''} disabled={!canEdit}
                onChange={e => setTarget(t => ({ ...t, [o.department]: e.target.value }))}
                data-testid={`recon-target-${o.department}`}
                className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white min-w-[190px]">
                <option value="">Move to department…</option>
                {profiles.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <select value={seniorFor[o.department] || ''} disabled={!canEdit}
                onChange={e => setSeniorFor(s => ({ ...s, [o.department]: e.target.value }))}
                data-testid={`recon-senior-${o.department}`}
                className="text-xs border border-slate-300 rounded-sm px-2 py-1.5 bg-white min-w-[170px]">
                <option value="">No senior profile</option>
                {seniors.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <button disabled={!canEdit || busy === o.department || !movable.length}
                onClick={() => reassign(o.department, movable.map(u => u.user_id))}
                data-testid={`recon-reassign-all-${o.department}`}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display font-bold uppercase tracking-wider text-white bg-[#3b4872] hover:bg-[#2d3856] rounded-sm disabled:opacity-40">
                {busy === o.department ? <Loader2 size={12} className="animate-spin" /> : <ArrowRight size={12} />}
                Move all {movable.length}
              </button>
            </div>

            {o.suggested_target && (
              <div className="px-4 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100">
                Closest existing profile: <strong>{o.suggested_target}</strong> — confirm before moving.
              </div>
            )}

            {open && (
              <div className="p-4">
                {selectedIds.length > 0 && (
                  <div className="mb-3 flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                    <span className="text-xs text-slate-600">{selectedIds.length} selected</span>
                    <button disabled={!canEdit || busy === o.department}
                      onClick={() => reassign(o.department, selectedIds)}
                      data-testid={`recon-reassign-selected-${o.department}`}
                      className="px-3 py-1 text-xs font-display font-bold uppercase tracking-wider text-white bg-[#3b4872] hover:bg-[#2d3856] rounded-sm disabled:opacity-40">
                      Move selected → {target[o.department] || '…'}
                    </button>
                  </div>
                )}
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-200">
                      <th className="py-2 w-8">
                        <input type="checkbox" data-testid={`recon-select-all-${o.department}`}
                          checked={movable.length > 0 && movable.every(u => picked[u.user_id])}
                          onChange={e => {
                            const v = e.target.checked;
                            setPicked(p => {
                              const n = { ...p };
                              movable.forEach(u => { n[u.user_id] = v; });
                              return n;
                            });
                          }} />
                      </th>
                      <th className="py-2">Name</th>
                      <th className="py-2">Designation</th>
                      <th className="py-2">Role</th>
                      <th className="py-2">Email</th>
                      <th className="py-2 text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.users.map(u => {
                      const rowKey = u.user_id || `emp-${u.employee_id}`;
                      return (
                      <tr key={rowKey} className="border-b border-slate-100" data-testid={`recon-user-${rowKey}`}>
                        <td className="py-2">
                          {u.user_id && (
                            <input type="checkbox" checked={!!picked[u.user_id]}
                              data-testid={`recon-check-${rowKey}`}
                              onChange={e => setPicked(p => ({ ...p, [u.user_id]: e.target.checked }))} />
                          )}
                        </td>
                        <td className="py-2 font-medium text-slate-800">{u.name || '—'}</td>
                        <td className="py-2 text-slate-600">{u.designation || '—'}</td>
                        <td className="py-2 text-slate-600">{u.role || (u.source === 'employee_no_login' ? 'no login' : '—')}</td>
                        <td className="py-2 text-slate-500">{u.email || '—'}</td>
                        <td className="py-2 text-right">
                          {u.user_id ? (
                            <button disabled={!canEdit || busy === o.department}
                              onClick={() => reassign(o.department, [u.user_id])}
                              data-testid={`recon-move-${rowKey}`}
                              className="px-2 py-1 text-[11px] font-bold uppercase tracking-wider text-[#3b4872] border border-[#3b4872] rounded-sm hover:bg-slate-50 disabled:opacity-40">
                              Move
                            </button>
                          ) : (
                            <span className="text-[10px] text-slate-400 italic" data-testid={`recon-nomove-${rowKey}`}>
                              No login — edit in Employee Master
                            </span>
                          )}
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const Stat = ({ label, value, tone, testid }) => (
  <div className={`border rounded-sm px-4 py-3 ${tone === 'rose' ? 'bg-rose-50 border-rose-200' : tone === 'emerald' ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-200'}`}
       data-testid={testid}>
    <div className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-lg font-display font-bold ${tone === 'rose' ? 'text-rose-700' : tone === 'emerald' ? 'text-emerald-700' : 'text-slate-800'}`}>{value}</div>
  </div>
);

export default DepartmentReconciliationPanel;
